import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { TableTabProps } from '@repo/ui/components/molecules/infinite-table';
import { useCommercialModules } from '@/lib/hooks/use-commercial-modules';

export const WAREHOUSE_FILTER_TAB_VALUES = {
  BALANCES: 'balances',
  MOVEMENTS: 'movements',
  PRODUCTS: 'products',
  INVENTORY: 'inventory',
  PRICE_LISTS: 'price-lists',
} as const;

export type WarehouseFilterTabValue =
  (typeof WAREHOUSE_FILTER_TAB_VALUES)[keyof typeof WAREHOUSE_FILTER_TAB_VALUES];

/**
 * Filtri del pannello delle singole sub-tab, azzerati a ogni cambio tab: un
 * periodo degli inventari o una categoria delle giacenze filtrerebbero
 * un'altra lista senza che nulla, nella UI, lo dica. Resta solo il deposito,
 * che è lo stesso concetto su giacenze, movimenti e inventari.
 */
export const WAREHOUSE_TAB_RESET_PARAMS = {
  dateFrom: undefined,
  dateTo: undefined,
  statuses: undefined,
  status: undefined,
  movementTypes: undefined,
  adjustmentsOnly: undefined,
  lowStockOnly: undefined,
  isActiveProduct: undefined,
  categoryId: undefined,
  companyId: undefined,
  activeOnly: undefined,
  sort: undefined,
} as const;

/** Le sub-tab del Magazzino, ciascuna con l'azzeramento dei filtri altrui. */
export function buildWarehouseFilterTabs<TData>(
  t: TFunction,
  options: { includeInventory?: boolean } = {}
): TableTabProps<TData>[] {
  const includeInventory = options.includeInventory ?? true;
  return (
    [
      {
        value: WAREHOUSE_FILTER_TAB_VALUES.BALANCES,
        text: t('warehouse.tabs.balances'),
      },
      {
        value: WAREHOUSE_FILTER_TAB_VALUES.MOVEMENTS,
        text: t('warehouse.tabs.movements'),
      },
      {
        value: WAREHOUSE_FILTER_TAB_VALUES.INVENTORY,
        text: t('warehouse.tabs.inventory', {
          defaultValue: 'Inventari fisici',
        }),
      },
      {
        value: WAREHOUSE_FILTER_TAB_VALUES.PRICE_LISTS,
        text: t('warehouse.tabs.price_lists', {
          defaultValue: 'Listini prezzo',
        }),
      },
    ]
      // Inventari fisici: solo con il modulo Magazzino nel contratto.
      .filter(
        (tab) =>
          includeInventory ||
          tab.value !== WAREHOUSE_FILTER_TAB_VALUES.INVENTORY
      )
      .map((tab) => ({ ...tab, params: { ...WAREHOUSE_TAB_RESET_PARAMS } }))
  );
}

/**
 * Sub-tab del modulo Magazzino: la navigazione la gestisce
 * `DataTableFilterTabs` via `synchWithUrlConfig.route = /warehouse` (stesso
 * meccanismo della pagina Documenti), spalmando nella search i `params` del
 * tab.
 */
export function useWarehouseFilterTabs<TData>(): TableTabProps<TData>[] {
  const { t } = useTranslation();
  const { isWarehouseIncluded } = useCommercialModules();
  return useMemo(
    () =>
      buildWarehouseFilterTabs<TData>(t, {
        includeInventory: isWarehouseIncluded,
      }),
    [t, isWarehouseIncluded]
  );
}
